import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import api from "services/api";
import Overlay from "components/Overlay/Overlay";

const ExpensesSubcategoryNewEdit = () => {
  usePageTitle(
    "overlayData?.subcategoryId" ? "Edit Subcategory" : "New Subcategory",
  );

  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const { overlayData, setOverlayName, showSnackbar } = useStateContext();

  const closeOverlay = () => setOverlayName("");

  const [subcategoryName, setSubcategoryName] = useState("");
  const [subcategoryDescription, setSubcategoryDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (overlayData) {
      setSubcategoryName(overlayData.subcategoryName || "");
      setSubcategoryDescription(overlayData.subcategoryDescription || "");
    }
  }, [overlayData]);

  const validateForm = () => {
    const newErrors = {};
    if (!subcategoryName.trim()) {
      newErrors.subcategoryName = "Subcategory name is required";
    }
    return newErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const subcategoryData = {
        CategoryID: overlayData.categoryId,
        Name: subcategoryName,
        Description: subcategoryDescription,
      };

      if (overlayData.subcategoryId) {
        await api.put(
          `/expenses/subcategories/${overlayData.subcategoryId}`,
          subcategoryData,
        );
      } else {
        await api.post("/expenses/subcategories", subcategoryData);
      }

      showSnackbar(
        overlayData.subcategoryId
          ? "Subcategory updated successfully"
          : "Subcategory created successfully",
        theme,
        "success",
      );

      if (overlayData.onSaved) {
        overlayData.onSaved();
      }
      closeOverlay();
    } catch (error) {
      console.error("Error saving subcategory:", error);
      showSnackbar("Error saving subcategory", theme, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const pageTitle = overlayData?.subcategoryId
    ? "Edit Expense Subcategory"
    : "New Expense Subcategory";

  const footer = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "end",
        alignItems: "center",
        width: "100%",
        flexDirection: "row",
        p: ".75rem 2rem",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "end",
          alignItems: "flex-end",
          width: "100%",
          flexDirection: "row",
        }}
      >
        {isMobile ? (
          <Tooltip title="Discard" placement="top">
            <IconButton
              color="primary"
              onClick={closeOverlay}
              sx={{
                width: "40px",
                height: "40px",
                border: `2px solid ${theme.palette.primary.main}`,
                borderRadius: "12px",
              }}
            >
              <DisabledByDefaultRoundedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            color="primary"
            variant="outlined"
            size="large"
            onClick={closeOverlay}
            sx={{
              borderRadius: "12px",
            }}
          >
            Discard
          </Button>
        )}

        <Box p="0 .5rem" />

        <Tooltip
          title={isMobile && isSubmitting ? "Saving changes..." : ""}
          placement="top"
        >
          <span>
            {isMobile ? (
              <IconButton
                color="primary"
                onClick={handleSubmit}
                disabled={isSubmitting}
                sx={{
                  width: "40px",
                  height: "40px",
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.background.default,
                  borderRadius: "12px",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "&.Mui-disabled": {
                    backgroundColor:
                      theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                <SaveRoundedIcon />
              </IconButton>
            ) : (
              <Button
                disabled={isSubmitting}
                color="primary"
                variant="contained"
                size="large"
                endIcon={<SaveRoundedIcon />}
                onClick={handleSubmit}
                sx={{
                  borderRadius: "12px",
                }}
              >
                {isSubmitting
                  ? "Saving..."
                  : overlayData?.subcategoryId
                    ? "Update"
                    : "Create"}
              </Button>
            )}
          </span>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Overlay
      id="expenses-subcategory-new-edit"
      open={true}
      onClose={closeOverlay}
      title={pageTitle}
      height="fit-content"
      footer={footer}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          p: "1rem 2.3rem 1rem 2.5rem",
          height: "100%",
          overflowY: "auto",
          width: "100%",
        }}
      >
          <TextField
            fullWidth
            label="Subcategory Name"
            value={subcategoryName}
            onChange={(e) => {
              setSubcategoryName(e.target.value);
              setErrors((prev) => {
                const { subcategoryName, ...rest } = prev;
                return rest;
              });
            }}
            error={!!errors.subcategoryName}
            helperText={errors.subcategoryName}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                "&.MuiInputBase-root fieldset": {
                  borderColor: theme.palette.divider,
                },
              },
            }}
          />

          <TextField
            fullWidth
            label="Description (Optional)"
            value={subcategoryDescription}
            onChange={(e) => setSubcategoryDescription(e.target.value)}
            multiline
            rows={3}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.MuiInputBase-root fieldset": {
                  borderColor: theme.palette.divider,
                },
              },
            }}
          />
        </Box>
      </Overlay>
  );
};

export default ExpensesSubcategoryNewEdit;
